import { useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Menu } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Sidebar } from "@/components/Sidebar";
import { currentMonthKey } from "@/lib/months";
import { useMonthMetrics } from "@/lib/useMonthMetrics";

import { useEffect } from "react";
import { useCurrentUser } from "@/lib/api";

const TITLES: Record<string, string> = {
  "/": "Overview",
  "/leads": "Lead Summary",
  "/nearby-campaigns": "Nearby Campaigns",
  "/ai-website-builder": "AI Website Builder Leads",
  "/goals": "Goal Tracking",
  "/efficiency": "Channel Efficiency",
  "/meta": "Meta Campaigns",
  "/linkedin-ads": "LinkedIn Campaigns",
  "/google-ads": "Google Ads",
  "/unidentified": "Unidentified Leads",
  "/webstore-analytics": "Webstore Analytics",
  "/marketing-updates": "Marketing Updates",
};

// Routes that are not backed by the monthly marketing pipeline. They fetch their own
// data on their own date range, so the month picker and the "no report for this
// month" empty state do not apply — showing either would block a page that has
// nothing to do with that pipeline run.
const SELF_CONTAINED_ROUTES = new Set(["/webstore-analytics"]);

export function Layout() {
  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthKey = searchParams.get("month") || currentMonthKey();

  useEffect(() => {
    if (!isUserLoading && !user) {
      window.location.href = "/login";
    }
  }, [isUserLoading, user]);

  const {
    payload,
    phase,
    error,
    monthLabel,
    startSync,
    job,
  } = useMonthMetrics(monthKey);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"month" | "current_week">("month");
  const location = useLocation();
  const title = TITLES[location.pathname] || "Overview";
  const isSelfContained = SELF_CONTAINED_ROUTES.has(location.pathname);

  const lastUpdated = payload?.last_updated;
  const mql = payload?.totals?.mql;
  const unidentified = payload?.still_unidentified?.length;
  const hasWeekly = !!payload?.weekly_reporting?.summary;

  const isLoading = phase === "loading";
  const isSyncing = phase === "starting" || phase === "fetching";
  const syncFailed = phase === "error";
  const showMissing = phase === "missing";
  const showLoadError = phase === "error" && !payload;

  function handleMonthChange(key: string) {
    if (key === monthKey) return;
    const next = new URLSearchParams(searchParams);
    if (key === currentMonthKey()) next.delete("month");
    else next.set("month", key);
    setSearchParams(next, { replace: false });
    // Reset viewMode if we change months
    setViewMode("month");
  }

  const outletContext = {
    payload,
    isLoading,
    error: error ? new Error(error) : null,
    phase,
    monthKey,
    monthLabel,
    startSync,
    viewMode,
  };

  if (isUserLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-canvas">
        <div className="text-sm text-slate-500 animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-canvas">
      <div className="hidden md:flex md:w-[240px] md:shrink-0 h-full">
        <Sidebar badges={{ mql, unidentified }} />
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm transition-opacity md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-[260px] bg-sidebar shadow-2xl flex flex-col md:hidden">
            <Sidebar badges={{ mql, unidentified }} onItemClick={() => setMobileOpen(false)} />
          </div>
        </>
      )}

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex items-center justify-between md:hidden border-b border-border bg-white px-4 py-3 shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="p-2 -ml-1.5 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-sm font-semibold text-ink-900 tracking-tight">Growth Insights</div>
          </div>
        </div>

        {!isSelfContained && (
          <PageHeader
            title={title}
            lastUpdated={lastUpdated}
            monthKey={monthKey}
            onMonthChange={handleMonthChange}
            isSyncing={isSyncing}
            syncFailed={syncFailed}
            jobError={job?.error || error}
            onSync={startSync}
            hasWeekly={hasWeekly}
            viewMode={viewMode}
            onViewModeChange={(mode) => setViewMode(mode)}
          />
        )}

        <div className="flex-1 overflow-y-auto bg-canvas px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          {isSelfContained ? (
            <Outlet context={outletContext} />
          ) : showMissing ? (
            <EmptyState
              title={`No report available for ${monthLabel}`}
              hint={`The ${monthLabel} report hasn't been generated yet. Click below to run the pipeline for this month.`}
              action={{
                label: isSyncing ? "Generating…" : `Generate report for ${monthLabel}`,
                onClick: startSync,
                disabled: isSyncing,
              }}
            />
          ) : showLoadError ? (
            <ErrorBanner message={error || "Couldn't load this month's report"} />
          ) : (
            <Outlet context={outletContext} />
          )}
        </div>
      </main>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-card border border-red-200 bg-red-50 px-4 py-3 mb-5 text-sm text-red-800">
      <div className="font-medium">Couldn't load report</div>
      <div className="text-xs mt-0.5 opacity-80">{message}</div>
    </div>
  );
}
